import { Info } from '@drift-labs/icons';
import {
	AMM_RESERVE_PRECISION_EXP,
	BigNum,
	BN,
	convertToNumber,
	PositionDirection,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	TEN_THOUSAND,
	MarketType,
	BASE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { EventEmitter } from 'events';
import React, { useEffect, useRef, useState } from 'react';
import Text from 'src/components/Text/Text';
import Tooltip from 'src/components/Tooltip/Tooltip';
import ValueDisplay from 'src/components/ValueDisplay';
import useAccountData from 'src/hooks/useAccountData';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { useBooleanLocalStorageState } from 'src/utils/localStorageUtils';
import NumLib from 'src/utils/NumLib';
import { ZERO } from '../../constants/constants';
import useInfoForCurrentlySelectedMarket from '../../hooks/useInfoForCurrentlySelectedMarket';
import { useTradeformPriceImpact } from '../../hooks/usePriceImpact';
import { useShowTradeformLiquidityWarnings } from '../../hooks/useShowLiquidityWarnings';
import useUserAccountIsReady from '../../hooks/useUserAccountIsReady';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import TradeformLiquidityWarning from '../TradeformLiquidityWarning';
import EstimatedProfit from './EstimatedProfit';
import { twMerge } from 'tailwind-merge';
import EstEntryPriceTooltip from './EstEntryPriceTooltip';
import UI_UTILS from 'src/utils/uiUtils';
import useCalculateTradeFee from 'src/hooks/useCalculateTradeFee';
import { COMMON_UI_UTILS, UIOrderType } from '@drift/common';
import MakerRebateTooltip from '../Tooltip/MakerRebateTooltip';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useIsLiteMode from 'src/hooks/useIsLiteMode';
// import Switch from '../Switch';

const liteRowClassName = 'w-full flex flex-row items-center justify-between';

const AdvancedPerpTradeSettings = (props: { orderType: string }) => {
	const isLiteMode = useIsLiteMode();

	const markPrice = useMarkPrice();

	const isPostOnly = useDriftStore(
		(s) => s.tradeForm.postOnly && s.tradeForm.orderType === 'limit'
	);

	const displayDecimals =
		useInfoForCurrentlySelectedMarket()?.info?.genericInfo?.displayDecimals;

	const {
		slippageTolerance,
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
	} = useDriftStore((s) => s.tradeForm);

	const currentAccount = useAccountData();

	const selectedMarketIndex = useDriftStore(
		(s) => s.selectedMarket.current.market.marketIndex
	);
	const selectedMarketName = useDriftStore((s) => s.selectedMarket.name);
	const side = useDriftStore((s) => s.tradeForm.side);
	const driftClientIsReady = useDriftClientIsReady();

	const currentPosition =
		currentAccount?.openPerpPositions?.find(
			(pos) => pos.marketIndex === selectedMarketIndex
		) ?? null;

	const quoteSize = Number(quoteSizeStringValue);

	const {
		entryPrice,
		priceImpact,
		priceImpactInputBaseSize,
		showPriceEstimateOracleDivergenceWarning,
	} = useTradeformPriceImpact();

	const getPriceImpactDisplayValue = () => {
		const priceImpactNum = BigNum.from(
			priceImpact ? priceImpact : 0,
			PRICE_PRECISION_EXP
		).toNum();

		const priceImpactPct = priceImpactNum * 100;

		if (priceImpactPct < 0.01) {
			return `<0.01`;
		} else {
			return priceImpactPct.toFixed(2);
		}
	};

	const defaultTakerFeeBps = Env.defaultTakerFeeBps;
	const { tradeFee, takerFeeBps } = useCalculateTradeFee({
		quoteSize: Number(quoteSizeStringValue) ?? 0,
		marketIndex: selectedMarketIndex,
		marketType: MarketType.PERP,
		isPostOnly: isPostOnly,
	});

	const getEntryPrice = () =>
		BigNum.from(entryPrice ? entryPrice : 0, PRICE_PRECISION_EXP);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [expectedPrice, setExpectedPrice] = useState(getEntryPrice());
	const [estimatedPriceImpact, setEstimatedPriceImpact] = useState('0%');
	const [marketAccount, setMarketAccount] = useState(null);
	const [liqPriceAfter, setLiqPrice] = useState(0);

	const userAccountIsReady = useUserAccountIsReady();

	const currentLeverage = currentAccount?.marginInfo?.leverage ?? 0;

	let currentLiquidationPrice = BigNum.from(
		currentPosition?.liqPrice ?? ZERO,
		PRICE_PRECISION_EXP
	).toNum();
	const driftClient = useDriftStore((s) => s.driftClient.client);

	currentLiquidationPrice =
		currentLiquidationPrice < 0 ? 0 : currentLiquidationPrice;

	useEffect(() => {
		if (driftClientIsReady) {
			setMarketAccount(driftClient?.getPerpMarketAccount(selectedMarketIndex));
		}
	}, [driftClientIsReady]);

	useEffect(() => {
		setEstimatedPriceImpact(`${getPriceImpactDisplayValue()}%`);
	}, [priceImpact?.toString()]);

	useEffect(() => {
		setExpectedPrice(getEntryPrice());
	}, [
		markPrice?.toString(),
		slippageTolerance,
		baseSizeStringValue,
		priceImpact?.toString(),
	]);

	const getLiqPrice = (
		estEntryPrice: BN,
		orderType: UIOrderType,
		isLong = true
	) => {
		if (!userAccountIsReady) return 0;
		if (!marketAccount) return 0;

		let baseAssetBN = BigNum.fromPrint(
			baseSizeStringValue,
			AMM_RESERVE_PRECISION_EXP
		).val;

		if (baseAssetBN.abs().gte(marketAccount?.amm.baseAssetReserve)) {
			return 0;
		}

		if (!isLong) {
			baseAssetBN = baseAssetBN.mul(new BN(-1));
		}

		let priceToUse;
		if (orderType === 'limit' && priceBoxStringValue) {
			const limitPriceBN = BigNum.fromPrint(
				priceBoxStringValue,
				PRICE_PRECISION_EXP
			).val;
			priceToUse = isLong
				? BN.min(estEntryPrice, limitPriceBN)
				: BN.max(estEntryPrice, limitPriceBN);
		} else {
			priceToUse = estEntryPrice;
		}

		const liqPriceBn = currentAccount
			? currentAccount.client.liquidationPrice(
					selectedMarketIndex,
					baseAssetBN,
					priceToUse
			  )
			: new BN(0);

		const liqPriceNum = NumLib.formatBn.toRawNum(liqPriceBn, PRICE_PRECISION);
		if (liqPriceNum <= 0) {
			return 0;
		}

		return Math.abs(Math.round(liqPriceNum * 100) / 100);
	};

	const previousBaseValue = BigNum.from(
		currentPosition?.baseSize,
		AMM_RESERVE_PRECISION_EXP
	);

	const baseSize = BigNum.fromPrint(
		baseSizeStringValue ? baseSizeStringValue : '0',
		AMM_RESERVE_PRECISION_EXP
	);

	const afterBaseValue = previousBaseValue.add(
		baseSize.mul(BigNum.from(side === 'buy' ? new BN(1) : new BN(-1)))
	);

	let leverageRatioAfterTrade;
	try {
		leverageRatioAfterTrade =
			!currentAccount ||
			!userAccountIsReady ||
			currentAccount.client?.getTotalCollateral() === ZERO
				? 0
				: convertToNumber(
						currentAccount.client?.accountLeverageRatioAfterTrade(
							selectedMarketIndex,
							MarketType.PERP,
							NumLib.formatNum.toQuoteBN(quoteSize),
							side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT
						) ?? ZERO,
						TEN_THOUSAND
				  );
	} catch (e) {
		console.error(e);
		leverageRatioAfterTrade = 0;
	}

	const previousDirection = previousBaseValue.lt(ZERO)
		? ' SHORT'
		: previousBaseValue.eq(ZERO)
		? ''
		: ' LONG';

	const afterDirection = afterBaseValue.lt(ZERO)
		? ' SHORT'
		: afterBaseValue.eq(ZERO)
		? ''
		: ' LONG';

	useEffect(() => {
		try {
			const newLiqPrice = getLiqPrice(
				entryPrice,
				props.orderType as UIOrderType,
				side === 'buy'
			);
			setLiqPrice(newLiqPrice);
		} catch (err) {
			console.error(err);
		}
	}, [
		selectedMarketIndex,
		markPrice?.toString(),
		baseSizeStringValue,
		entryPrice?.toString(),
		props.orderType,
		side,
	]);

	const [hasShownJitTooltip, setHasShownJitTooltip] =
		useBooleanLocalStorageState('HasShownJitExplanationTooltip', false);

	const jitTooltipEmitter = useRef(new EventEmitter());

	// Show the JIT tooltip if the user has interacted with the trade form and they haven't seen it before
	useEffect(() => {
		if (priceImpact && priceImpact.gt(ZERO) && !hasShownJitTooltip) {
			jitTooltipEmitter.current.emit('event');
			setHasShownJitTooltip(true);
		}
	}, [hasShownJitTooltip, priceImpact?.toString()]);

	const showLiquidityWarningState = useShowTradeformLiquidityWarnings();

	// check if price impact is up to date, allow 1% leeway for small fluctuations
	const priceImpactIsCurrent = BigNum.fromPrint(
		baseSizeStringValue,
		BASE_PRECISION_EXP
	)
		.sub(BigNum.from(priceImpactInputBaseSize, BASE_PRECISION_EXP))
		.abs()
		.lte(
			BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP).scale(1, 100)
		);

	const limitExitSize = BigNum.fromPrint(
		baseSizeStringValue,
		BASE_PRECISION_EXP
	);
	const currentPositionBaseSize = currentPosition
		? BigNum.from(currentPosition.baseSize, BASE_PRECISION_EXP).abs()
		: BigNum.zero(BASE_PRECISION_EXP);

	return (
		<>
			<div className="flex flex-col w-full space-y-2 rounded-md advanced-trade-details">
				{props.orderType === 'limit' &&
					currentPosition &&
					limitExitSize.lte(currentPositionBaseSize) && (
						<EstimatedProfit
							currentPositionSize={currentPositionBaseSize}
							currentPositionEntryPrice={BigNum.from(
								currentPosition.entryPrice,
								PRICE_PRECISION_EXP
							)}
							currentPositionDirection={currentPosition.direction}
							side={side}
							limitExitSize={limitExitSize}
							limitExitPrice={BigNum.fromPrint(
								priceBoxStringValue,
								PRICE_PRECISION_EXP
							)}
						></EstimatedProfit>
					)}

				{props.orderType === 'market' &&
					showLiquidityWarningState.state !== 'hide' && (
						<TradeformLiquidityWarning
							warningState={showLiquidityWarningState}
						/>
					)}

				{props.orderType === 'market' ? (
					<>
						<AdvancedTradeSettingsRenderer
							baseSizeStringValue={baseSizeStringValue}
							jitTooltipEmitter={jitTooltipEmitter.current}
							expectedPrice={
								baseSizeStringValue &&
								Number(baseSizeStringValue) > 0 &&
								expectedPrice &&
								expectedPrice.gt(ZERO) &&
								priceImpactIsCurrent
									? expectedPrice.toFixed(displayDecimals)
									: undefined
							}
							priceImpactIsCurrent={priceImpactIsCurrent}
							priceImpact={priceImpact ? priceImpact.toNumber() : undefined}
							estimatedPriceImpact={
								priceImpact &&
								Number(baseSizeStringValue) > 0 &&
								baseSizeStringValue &&
								priceImpactIsCurrent
									? priceImpact.gt(ZERO)
										? estimatedPriceImpact
										: undefined
									: undefined
							}
							liqPriceAfter={liqPriceAfter}
							currentLiquidationPrice={currentLiquidationPrice}
							selectedMarketIndex={selectedMarketIndex}
							userAccountIsReady={userAccountIsReady}
							currentLeverage={currentLeverage}
							leverageRatioAfterTrade={leverageRatioAfterTrade}
							overrideNoValueChange={!baseSize || baseSize.eqZero()}
							showPriceEstimateOracleDivergenceWarning={
								showPriceEstimateOracleDivergenceWarning
							}
						/>
					</>
				) : (
					<></>
				)}

				{/* Liquidation limit price */}
				{props.orderType === 'limit' && (
					<ValueDisplay.ValueChange
						label="Est. Liquidation Price"
						previousValue={currentLiquidationPrice}
						afterValue={liqPriceAfter}
						previousValuePrint={
							currentLiquidationPrice !== 0
								? currentLiquidationPrice.toLocaleString('en-US', {
										maximumSignificantDigits: 5,
								  })
								: 'None'
						}
						afterValuePrint={
							liqPriceAfter !== 0
								? COMMON_UI_UTILS.trimTrailingZeros(
										UI_UTILS.toNotional(liqPriceAfter, 5)
								  )
								: 'None'
						}
						forceWhite
						nullOverride={
							!userAccountIsReady ||
							isNaN(selectedMarketIndex) ||
							(!currentLiquidationPrice && !liqPriceAfter)
						}
						customOverride={
							<SkeletonValuePlaceholder
								className="h-[14px] w-[50px]"
								loading={false}
							/>
						}
						overrideNoValueChange={!baseSize || baseSize.eqZero()}
					/>
				)}

				{!isLiteMode && (
					<ValueDisplay.ValueChange
						label="Position"
						previousValue={previousBaseValue.abs().toNum()}
						afterValue={afterBaseValue.abs().toNum()}
						previousValuePrint={`${previousBaseValue
							.abs()
							.toNum()
							.toLocaleString('en-US', {
								maximumSignificantDigits: 5,
							})} ${previousDirection}`}
						afterValuePrint={`${afterBaseValue
							.abs()
							.toNum()
							.toLocaleString('en-US', {
								maximumSignificantDigits: 5,
							})} ${afterDirection}`}
						forceWhite
					/>
				)}

				{isPostOnly && (
					<Text.BODY3 className="flex items-end justify-between w-full">
						<div
							className="flex items-center text-text-secondary"
							title="Entry price slippage from mark price."
						>
							<Text.BODY3>Maker Rebate </Text.BODY3>
							<MakerRebateTooltip />
						</div>

						<div className="font-numeral text-text-default">
							~
							<span className="ml-1.5 text-positive-green">
								{tradeFee.abs().toNotional()}
							</span>
						</div>
					</Text.BODY3>
				)}

				{!isPostOnly &&
					(props.orderType === 'market' || props.orderType === 'limit') &&
					takerFeeBps < defaultTakerFeeBps && (
						<Tooltip
							allowHover
							showArrow
							placement={'right'}
							content={
								<div>
									Taker fees for {selectedMarketName} have been reduced from{' '}
									{defaultTakerFeeBps}
									bps to {takerFeeBps}bps.
								</div>
							}
						>
							<ValueDisplay.Default
								label={
									<Text.BODY3>
										Fees{' '}
										<Info
											size={14}
											className="relative mt-[-4px] top-[3px] ml-0.5"
										/>
									</Text.BODY3>
								}
								value={
									tradeFee.toFixed(2) === '0.00' ? (
										<Text.BODY3>0</Text.BODY3>
									) : (
										<div>
											<Text.BODY3 className="line-through text-text-secondary">
												$
												{(
													(defaultTakerFeeBps / takerFeeBps) *
													tradeFee.toNum()
												).toFixed(2)}
											</Text.BODY3>{' '}
											<Text.BODY3>${tradeFee.toFixed(2)}</Text.BODY3>
										</div>
									)
								}
							/>
						</Tooltip>
					)}
			</div>
		</>
	);
};

/**
 * Created this component whose only inputs are the "actual" numbers to display .. did this because the parent component re-renders a lot due to values changing in the store, without the human-readable values on the screen necessarily changing
 * @param param0
 * @returns
 * *
 */
const AdvancedTradeSettingsRendererComponent = ({
	jitTooltipEmitter,
	baseSizeStringValue,
	expectedPrice,
	priceImpactIsCurrent,
	estimatedPriceImpact,
	priceImpact,
	currentLiquidationPrice,
	liqPriceAfter,
	selectedMarketIndex,
	userAccountIsReady,
	currentLeverage,
	leverageRatioAfterTrade,
	overrideNoValueChange,
	showPriceEstimateOracleDivergenceWarning,
}: {
	jitTooltipEmitter: EventEmitter;
	baseSizeStringValue: string;
	expectedPrice?: string;
	priceImpactIsCurrent?: boolean;
	estimatedPriceImpact?: string;
	priceImpact?: number;
	currentLiquidationPrice: number;
	liqPriceAfter: number;
	selectedMarketIndex: number;
	userAccountIsReady: boolean;
	currentLeverage: number;
	leverageRatioAfterTrade: number;
	overrideNoValueChange: boolean;
	showPriceEstimateOracleDivergenceWarning: boolean;
}) => {
	const isLiteMode = useIsLiteMode();
	const isMobile = useIsMobileScreenSize();

	const estEntryPriceElement = (
		<ValueDisplay.Default
			warn={showPriceEstimateOracleDivergenceWarning}
			label={
				<span
					className={twMerge(
						showPriceEstimateOracleDivergenceWarning && 'text-warn-yellow'
					)}
				>
					Est. Entry Price
					<Info size={14} className="relative top-[3px] left-1" />
				</span>
			}
			value={
				!overrideNoValueChange && expectedPrice && priceImpactIsCurrent ? (
					<>
						{COMMON_UI_UTILS.trimTrailingZeros(
							UI_UTILS.toNotional(Number(expectedPrice), 5),
							2
						)}
					</>
				) : (
					<SkeletonValuePlaceholder
						className="h-[14px] w-[50px]"
						loading={Number(baseSizeStringValue) > 0}
					/>
				)
			}
			rightPadding={'pt-[2px]'}
		/>
	);

	return isLiteMode ? (
		<>
			<div className={liteRowClassName}>
				<Text.BODY2 className="text-text-label">
					Est. Liquidation Price
				</Text.BODY2>
				<Text.BODY2 className="text-text-default">
					{baseSizeStringValue && baseSizeStringValue !== '0'
						? liqPriceAfter !== 0
							? COMMON_UI_UTILS.trimTrailingZeros(
									UI_UTILS.toNotional(liqPriceAfter, 5)
							  )
							: 'None'
						: ''}
				</Text.BODY2>
			</div>
			{/* <div className={liteRowClassName}>
				<Text.BODY2 className="text-text-label">Show Confirmation</Text.BODY2>
				<Switch checked={false} onChange={() => {}} />
			</div> */}
		</>
	) : (
		<>
			{isMobile ? (
				<>{estEntryPriceElement}</>
			) : (
				<EstEntryPriceTooltip
					placement="right"
					showPriceEstimateOracleDivergenceWarning={
						showPriceEstimateOracleDivergenceWarning
					}
					childElement={estEntryPriceElement}
					manualEmitter={jitTooltipEmitter}
				/>
			)}

			<ValueDisplay.Default
				label="Est. Price Impact"
				value={
					priceImpact !== undefined &&
					!overrideNoValueChange &&
					priceImpactIsCurrent ? (
						priceImpact > 0 ? (
							<>{estimatedPriceImpact}</>
						) : (
							<>{'0%'}</>
						)
					) : (
						<SkeletonValuePlaceholder
							className="h-[14px] w-[50px]"
							loading={!overrideNoValueChange}
						/>
					)
				}
			/>

			<ValueDisplay.ValueChange
				label="Est. Liquidation Price"
				previousValue={currentLiquidationPrice}
				afterValue={liqPriceAfter}
				previousValuePrint={
					currentLiquidationPrice !== 0
						? currentLiquidationPrice.toLocaleString('en-US', {
								maximumSignificantDigits: 5,
						  })
						: 'None'
				}
				afterValuePrint={
					liqPriceAfter !== 0
						? COMMON_UI_UTILS.trimTrailingZeros(
								UI_UTILS.toNotional(liqPriceAfter, 5)
						  )
						: 'None'
				}
				forceWhite
				nullOverride={
					!userAccountIsReady ||
					isNaN(selectedMarketIndex) ||
					(!currentLiquidationPrice && !liqPriceAfter)
				}
				customOverride={
					<SkeletonValuePlaceholder
						className="h-[14px] w-[50px]"
						loading={false}
					/>
				}
				overrideNoValueChange={overrideNoValueChange}
			/>

			<ValueDisplay.ValueChange
				label="Acct. Leverage"
				rightSymbol="x"
				previousValue={currentLeverage}
				afterValue={leverageRatioAfterTrade}
				previousValuePrint={currentLeverage.toLocaleString('en-US', {
					maximumSignificantDigits: 3,
				})}
				afterValuePrint={leverageRatioAfterTrade.toLocaleString('en-US', {
					maximumSignificantDigits: 3,
				})}
				forceWhite
				nullOverride={
					(!userAccountIsReady ||
						(!currentLeverage && !leverageRatioAfterTrade) ||
						isNaN(selectedMarketIndex)) &&
					true
				}
				customOverride={
					<SkeletonValuePlaceholder
						className="h-[14px] w-[50px]"
						loading={false}
					/>
				}
				overrideNoValueChange={overrideNoValueChange}
			/>
		</>
	);
};

const AdvancedTradeSettingsRenderer = React.memo(
	AdvancedTradeSettingsRendererComponent
);

export default React.memo(AdvancedPerpTradeSettings);
